import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Drawer,
    Typography,
    Divider,
    Slider,
    FormControlLabel,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch,
    IconButton,
    Chip,
    Stack,
    Button
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { ref, get } from "firebase/database";
import { getProductCategorias } from "../utils/productCategories";
import { rtdb } from "../services/firebase";

// Função auxiliar (mesma do pai para consistência)
const converterPreco = (precoStr) => {
    if (!precoStr) return 0;
    const limpo = precoStr.toString().replace(/[^\d,.]/g, "");
    const numero = parseFloat(limpo.replace(",", "."));
    return isNaN(numero) ? 0 : numero;
};

export default function FiltrosSidebar({
    mobile = false,
    open,
    onClose,
    produtos = [], // Todos os produtos (para calcular max price e counts)
    filtrosAtuais, // Recebido do pai
    onFiltersChange,
    marcasDb, // Função para atualizar o pai
}) {
    const theme = useTheme();
    const [categoriasDb, setCategoriasDb] = useState([]);
    const [loadingCats, setLoadingCats] = useState(true);

    // Estado LOCAL apenas para o slider (para não travar a renderização enquanto arrasta)
    const [sliderVisual, setSliderVisual] = useState(filtrosAtuais.preco);

    // 1. Carregar Categorias do Firebase
    useEffect(() => {
        async function carregarCategorias() {
            try {
                const appSnap = await get(ref(rtdb, "app/category"));
                if (appSnap.exists()) {
                    const cats = appSnap.val();
                    setCategoriasDb(Array.isArray(cats) ? cats : []);
                }
            } catch (err) {
                console.error("Erro categorias:", err);
            } finally {
                setLoadingCats(false);
            }
        }
        carregarCategorias();
    }, []);

    // 2. Calcular Preço Máximo e Contagem por Categoria / Marca dinamicamente
    const { maxPreco, categoryCounts, brandCounts } = useMemo(() => {
      let max = 0;
      const counts = {};
      const bCounts = {};

      // zera categorias do DB
      categoriasDb.forEach((c) => (counts[c] = 0));

      // zera marcas do DB (igual categorias)
      (marcasDb || []).forEach((m) => (bCounts[m] = 0));

      produtos.forEach((p) => {
        const preco = p.precos?.isPromo && p.precos?.promo
          ? converterPreco(p.precos.promo)
          : converterPreco(p.precos?.normal);

        if (preco > max) max = preco;

        getProductCategorias(p).forEach((cat) => {
          counts[cat] = (counts[cat] || 0) + 1;
        });

        const marca = (p.marca || "").trim();
        if (marca) {
          // se a marca existe no DB, incrementa; se não existir, ainda assim conta (pra não “sumir” produto)
          bCounts[marca] = (bCounts[marca] ?? 0) + 1;
        }
      });

      return {
        maxPreco: Math.ceil(max / 100) * 100 || 1000,
        categoryCounts: counts,
        brandCounts: bCounts
      };
    }, [produtos, categoriasDb, marcasDb]);

    // 3. Sincronizar slider visual quando o filtro externo muda (ex: botão limpar)
    useEffect(() => {
        if (filtrosAtuais?.preco) {
            setSliderVisual(filtrosAtuais.preco);
        }
    }, [filtrosAtuais?.preco]);

    // --- HANDLERS ---

    // Atualiza apenas o visual enquanto arrasta
    const handleSliderChange = (event, newValue) => {
        setSliderVisual(newValue);
    };

    // Atualiza o filtro real quando solta o slider
    const handleSliderCommit = (event, newValue) => {
        onFiltersChange({ ...filtrosAtuais, preco: newValue });
    };

    const handleCategoriaToggle = (cat) => {
        const selecionadas = filtrosAtuais.categorias || [];
        const novasCategorias = selecionadas.includes(cat)
            ? selecionadas.filter((c) => c !== cat)
            : [...selecionadas, cat];

        onFiltersChange({ ...filtrosAtuais, categorias: novasCategorias });
    };

    const handleMarcaToggle = (marca) => {
      const selecionadas = filtrosAtuais.marcas || [];
      const novasMarcas = selecionadas.includes(marca)
        ? selecionadas.filter((m) => m !== marca)
        : [...selecionadas, marca];

      onFiltersChange({ ...filtrosAtuais, marcas: novasMarcas });
    };

    const handleSwitchChange = (field) => (event) => {
        onFiltersChange({ ...filtrosAtuais, [field]: event.target.checked });
    };

    const limparTudo = () => {
        onFiltersChange({
            preco: [0, maxPreco],
            categorias: [],
            marcas: [],
            promocoes: false,
            emEstoque: false,
        });
    };

    // --- RENDER CONTENT ---
    const SidebarContent = (
        <Box
            sx={{
                width: mobile ? "100%" : "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
            }}
        >
            {/* HEADER */}
            <Box sx={{ p: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6" fontWeight={700} color="primary">
                    Filtrar Resultados
                </Typography>
                {mobile && (
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>

            {/* CONTEÚDO SCROLLÁVEL */}
            <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2.5, pb: 4 }}>

                {/* Botão Resetar (se houver filtros ativos) */}
                {(filtrosAtuais.categorias.length > 0 || (filtrosAtuais.marcas?.length || 0) > 0 || filtrosAtuais.promocoes || filtrosAtuais.emEstoque) && (
                    <Button
                        startIcon={<FilterListOffIcon />}
                        onClick={limparTudo}
                        fullWidth
                        size="small"
                        color="inherit"
                        sx={{ mb: 2, bgcolor: "action.hover" }}
                    >
                        Limpar Filtros
                    </Button>
                )}

                {/* ========== PREÇO ========== */}
                <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                        <Typography fontWeight={600}>Faixa de Preço</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1, pt: 0, pb: 3 }}>
                        <Slider
                            value={sliderVisual}
                            onChange={handleSliderChange}
                            onChangeCommitted={handleSliderCommit}
                            valueLabelDisplay="auto"
                            min={0}
                            max={maxPreco}
                            valueLabelFormat={(v) => `R$ ${v}`}
                            sx={{
                                color: "primary.main",
                                '& .MuiSlider-thumb': {
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }
                            }}
                        />
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                            <Typography variant="caption" color="text.primary">
                                R$ {sliderVisual[0]}
                            </Typography>
                            <Typography variant="caption" color="text.primary">
                                R$ {sliderVisual[1]}
                            </Typography>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                <Divider sx={{ my: 1 }} />

                {/* ========== STATUS (SWITCHES) ========== */}
                <Box sx={{ py: 2 }}>
                    <Typography fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                        Status
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="body2">Apenas Promoções</Typography>
                        <Switch
                            checked={filtrosAtuais.promocoes}
                            onChange={handleSwitchChange('promocoes')}
                            size="small"
                        />
                    </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* ========== CATEGORIAS ========== */}
                <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                        <Typography fontWeight={600}>Categorias</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        {loadingCats ? (
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>Carregando...</Typography>
                        ) : (
                            categoriasDb.map((cat) => {
                                const count = categoryCounts[cat] || 0;
                                const disabled = count === 0;
                                const isSelected = filtrosAtuais.categorias.includes(cat);

                                return (
                                    <FormControlLabel
                                        key={cat}

                                        //preciso trocar a cor da linha do checkbox pra cinza, quando nao esta selecionado
                                        sx={{
                                            width: '100%',
                                            m: 0,
                                            py: 0.5,

                                            justifyContent: 'space-between',
                                            flexDirection: 'row-reverse', // Checkbox na direita
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }
                                        }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                disabled={disabled}
                                                checked={isSelected}
                                                onChange={() => handleCategoriaToggle(cat)}
                                                sx={{
                                                    p: 0.5,

                                                    // cor quando NÃO está marcado
                                                    color: 'rgba(0,0,0,0.35)',

                                                    // cor quando está marcado
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },

                                                    // se quiser tratar hover também
                                                    '&:hover': {
                                                        bgcolor: 'transparent',
                                                    },

                                                    // quando estiver disabled
                                                    '&.Mui-disabled': {
                                                        color: 'rgba(0,0,0,0.2)',
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <Typography variant="body2" color={disabled ? "text.disabled" : "text.primary"} sx={{ mr: 1, fontWeight: isSelected ? 600 : 400 }}>
                                                    {cat}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                );
                            })
                        )}
                    </AccordionDetails>
                </Accordion>

                <Divider sx={{ my: 1 }} />

                {/* ========== MARCAS ========== */}
                <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                    <Typography fontWeight={600}>Marcas</Typography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 0, pt: 0 }}>
                    {(marcasDb || []).length === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                        Nenhuma marca encontrada.
                      </Typography>
                    ) : (
                      (marcasDb || []).map((marca) => {
                          const count = brandCounts[marca] || 0;
                          const disabled = count === 0;
                          const isSelected = (filtrosAtuais.marcas || []).includes(marca);

                          return (
                            <FormControlLabel
                              key={marca}
                              sx={{
                                width: "100%",
                                m: 0,
                                py: 0.5,
                                justifyContent: "space-between",
                                flexDirection: "row-reverse",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.02)", borderRadius: 1 }
                              }}
                              control={
                                <Checkbox
                                  size="small"
                                  disabled={disabled}
                                  checked={isSelected}
                                  onChange={() => handleMarcaToggle(marca)}
                                  sx={{
                                        p: 0.5,

                                        // cor quando NÃO está marcado
                                        color: 'rgba(0,0,0,0.35)',

                                        // cor quando está marcado
                                        '&.Mui-checked': {
                                            color: 'primary.main',
                                        },

                                        // se quiser tratar hover também
                                        '&:hover': {
                                            bgcolor: 'transparent',
                                        },

                                        // quando estiver disabled
                                        '&.Mui-disabled': {
                                            color: 'rgba(0,0,0,0.2)',
                                        },
                                    }}
                                />
                              }
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                  <Typography
                                    variant="body2"
                                    color={disabled ? "text.disabled" : "text.primary"}
                                    sx={{ mr: 1, fontWeight: isSelected ? 600 : 400 }}
                                  >
                                    {marca}
                                  </Typography>

                                  <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                                    {count}
                                  </Typography>
                                </Box>
                              }
                            />
                          );
                        })
                    )}
                  </AccordionDetails>
                </Accordion>

            </Box>

            {/* FOOTER MOBILE (Botão Aplicar) */}
            {mobile && (
                <Box sx={{ p: 2, borderTop: "1px solid rgba(0,0,0,0.1)" }}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={onClose}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        Ver resultados
                    </Button>
                </Box>
            )}
        </Box>
    );

    if (mobile) {
        return (
            <Drawer
                anchor="right" // Em mobile modernos, sidebar na direita é comum (opcional)
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: { width: "85vw", maxWidth: 360 }
                }}
            >
                {SidebarContent}
            </Drawer>
        );
    }

    return SidebarContent;
}